import { useEffect, useMemo, useState, type FormEvent, type SyntheticEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/components/site/seo-copy";
import { fetchPublicJobs, companyName, type Job } from "@/components/site/jobs";
import JobsView, { JobCard, JobsPromo } from "@/components/cl/views/JobsView";

export const Route = createFileRoute("/find-jobs")({
  head: () => pageHead("/find-jobs"),
  component: FindJobs,
});

type Filters = {
  q: string;
  where: string;
  cat: string;
  contract: string;
  setting: string;
  minPay: number;
  maxPay: number;
  commissionOnly: boolean;
  sectors: string[];
  sort: string;
};

const EMPTY: Filters = {
  q: "",
  where: "",
  cat: "All roles",
  contract: "Any contract",
  setting: "Any setting",
  minPay: 0,
  maxPay: 0,
  commissionOnly: false,
  sectors: [],
  sort: "Newest",
};
const PAGE = 8;

const baseK = (comp: string) => {
  const k = comp.match(/£\s*(\d+(?:\.\d+)?)k/i);
  if (k) return Number(k[1]);
  const hr = comp.match(/£\s*(\d+(?:\.\d+)?)\s*\/\s*hr/i);
  if (hr) return Math.round((Number(hr[1]) * 37.5 * 52) / 1000);
  const full = comp.match(/£\s*([\d,]{4,})/);
  if (full) return Math.round(Number(full[1]!.replace(/,/g, "")) / 1000);
  return 0;
};
const postedDays = (posted: string) => {
  const n = Number(posted.match(/\d+/)?.[0] ?? 0);
  if (/week/i.test(posted)) return n * 7;
  if (/month/i.test(posted)) return n * 30;
  return n;
};
const recency = (j: Job) =>
  j.createdAt ? new Date(j.createdAt).getTime() : -postedDays(j.posted) * 86_400_000;

const sectorWords: Record<string, string[]> = {
  "Financial services": ["financial", "finance", "mortgage", "insurance", "wealth", "pension"],
  "Marketing and agencies": ["marketing", "agency", "advertising"],
  "SaaS and technology": ["saas", "software", "technology", "tech", "cyber"],
  Recruitment: ["recruit"],
  "Charity and fundraising": ["charity", "fundrais"],
  "Property and construction": ["property", "estate", "lettings", "construction"],
  "Coaching and consulting": ["coaching", "training", "consult"],
  "Telecoms and energy": ["telecom", "energy"],
  "Education and training": ["education", "training", "course"],
  "Health and care": ["health", "care"],
  "Media and advertising": ["media", "advertising", "events"],
  "Retail and ecommerce": ["retail", "ecommerce", "e-commerce"],
};

function matchesRole(j: Job, cat: string) {
  if (cat === "All roles") return true;
  const roleHay = `${j.title} ${j.seniority}`.toLowerCase();
  const t = cat.toLowerCase();
  if (t.startsWith("sdr")) return /sdr|bdr|outbound/.test(roleHay);
  if (t === "high-ticket closer") return /closer|high.?ticket/.test(roleHay);
  if (t === "business development manager") return /business development|bdr/.test(roleHay);
  if (t === "appointment setter") return roleHay.includes("setter");
  if (t === "recruitment consultant") return roleHay.includes("recruit");
  if (t === "fundraiser") return roleHay.includes("fundrais");
  return roleHay.includes(t);
}

/** Reads every control in the ported page (they're plain uncontrolled inputs). */
function readFilters(root: HTMLElement): Filters {
  const checked = (name: string) => {
    const el = root.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`);
    return el?.parentElement?.querySelector("span")?.textContent?.trim() ?? "";
  };
  const salary = checked("sl");
  const range = salary.match(/£(\d+)k to £(\d+)k/i);
  const min = salary.match(/£(\d+)k\+/i);
  return {
    q:
      root.querySelector<HTMLInputElement>('input[type="search"]')?.value.trim().toLowerCase() ??
      "",
    where:
      root
        .querySelector<HTMLInputElement>('input.jb-in[type="text"]')
        ?.value.trim()
        .toLowerCase() ?? "",
    cat: checked("jb-cat") || "All roles",
    contract: checked("ct") || "Any contract",
    setting: checked("st") || "Any setting",
    minPay: range ? Number(range[1]) : min ? Number(min[1]) : 0,
    maxPay: range ? Number(range[2]) : 0,
    commissionOnly: salary === "Commission only",
    sectors: Array.from(root.querySelectorAll<HTMLInputElement>('input[name="sc"]:checked')).map(
      (i) => i.parentElement?.querySelector("span")?.textContent?.trim() ?? "",
    ),
    sort: root.querySelector<HTMLSelectElement>("select")?.value ?? "Newest",
  };
}

function FindJobs() {
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [f, setF] = useState<Filters>(EMPTY);
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    let cancelled = false;
    fetchPublicJobs()
      .then((live) => !cancelled && setAllJobs(live))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const list = allJobs.filter((j) => {
      const hay = `${j.title} ${companyName(j)} ${j.seniority} ${j.advert}`.toLowerCase();
      if (f.q && !hay.includes(f.q)) return false;
      if (f.where && !`${j.location} ${j.setting}`.toLowerCase().includes(f.where)) return false;
      if (
        f.contract !== "Any contract" &&
        !j.contract.toLowerCase().includes(f.contract.toLowerCase())
      )
        return false;
      if (f.setting !== "Any setting" && !j.setting.toLowerCase().includes(f.setting.toLowerCase()))
        return false;
      if (f.commissionOnly) {
        if (!/commission only/i.test(`${j.payStructure} ${j.pay}`)) return false;
      } else if (f.minPay) {
        const salary = baseK(j.pay);
        if (salary < f.minPay || (f.maxPay > 0 && salary > f.maxPay)) return false;
      }
      if (f.sectors.length) {
        const sh = `${j.title} ${j.seniority} ${j.advert}`.toLowerCase();
        if (
          !f.sectors.some((s) => (sectorWords[s] ?? [s.toLowerCase()]).some((w) => sh.includes(w)))
        )
          return false;
      }
      return matchesRole(j, f.cat);
    });
    return f.sort === "Highest pay"
      ? [...list].sort((a, b) => baseK(b.pay) - baseK(a.pay))
      : [...list].sort((a, b) => Number(!!b.featured) - Number(!!a.featured) || recency(b) - recency(a));
  }, [allJobs, f]);

  const visible = filtered.slice(0, shown);
  const cards = visible.flatMap((j, i) => {
    const company = companyName(j);
    const initials = company
      .split(/\s+/)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
    const card = (
      <JobCard
        key={j.id}
        j={j}
        initials={initials}
        company={company}
        posted={j.posted.replace(/^Posted /, "")}
      />
    );
    return i === 2 && filtered.length > 3 ? [card, <JobsPromo key="promo" />] : [card];
  });

  const sync = (e: SyntheticEvent<HTMLElement>) => {
    setF(readFilters(e.currentTarget));
    setShown(PAGE);
  };

  return (
    <div
      onChange={sync}
      onSubmit={(e: FormEvent<HTMLElement>) => {
        e.preventDefault();
        sync(e);
      }}
      onClick={(e) => {
        const root = e.currentTarget;
        if ((e.target as HTMLElement).closest("summary span")) {
          e.preventDefault();
          root.querySelectorAll<HTMLInputElement>("aside input").forEach((i) => {
            i.checked = i.type === "radio" && !!i.parentElement?.textContent?.startsWith("Any");
          });
          setF(readFilters(root));
        }
      }}
    >
      <JobsView
        v={{
          cards: cards.length ? (
            cards
          ) : (
            <p style={{ margin: 0, padding: "32px 0", fontSize: 18, color: "#5A5A60" }}>
              {allJobs.length
                ? "No roles match those filters."
                : "No live roles right now. Check back soon."}
            </p>
          ),
          count: filtered.length,
          hasMore: filtered.length > shown,
          more: () => setShown((n) => n + PAGE),
        }}
      />
    </div>
  );
}
